import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (!marqueeItems.length) return;

    this.#syncMarquee();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      this.#resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.#handleResize, { passive: true });
    }

    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #handleResize = debounce(() => {
    this.#syncMarquee({ restartAnimation: true });
  }, 250);

  #syncMarquee({ restartAnimation = false } = {}) {
    const measurement = this.#measure();
    if (!measurement) return;

    const { targetCopies, speed } = measurement;
    const didChangeCopies = this.#renderRepeatedItems(targetCopies);

    if (didChangeCopies || !this.clonedContent) {
      this.#duplicateContent();
    }

    this.#setSpeed(speed);

    if (restartAnimation) {
      this.#restartAnimation();
    }
  }

  #measure() {
    const baseItem = this.refs.content.firstElementChild;
    if (!baseItem) return null;

    const marqueeWidth = this.offsetWidth || this.getBoundingClientRect().width || 1;
    const itemWidth = baseItem.getBoundingClientRect().width || 1;
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 1;

    const targetCopies = Math.max(1, Math.ceil(marqueeWidth / itemWidth));
    const speed = Math.sqrt(targetCopies) * speedFactor;

    return {
      targetCopies,
      speed,
    };
  }

  #setSpeed(value) {
    const nextValue = `${value}s`;
    if (this.style.getPropertyValue('--marquee-speed') === nextValue) return;

    this.style.setProperty('--marquee-speed', nextValue);
  }

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();
    if (!animations.length) return;

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    clone.querySelectorAll('[ref]').forEach((node) => {
      node.removeAttribute('ref');
    });

    this.refs.wrapper.appendChild(clone);
  }

  #renderRepeatedItems(targetCopies) {
    const { content } = this.refs;
    const baseItem = content.firstElementChild;

    if (!baseItem) return false;

    let currentCopies = content.children.length;
    let changed = false;

    while (currentCopies < targetCopies) {
      content.appendChild(baseItem.cloneNode(true));
      currentCopies += 1;
      changed = true;
    }

    while (currentCopies > targetCopies && content.children.length > 1) {
      content.lastElementChild?.remove();
      currentCopies -= 1;
      changed = true;
    }

    return changed;
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}